/**
 * Single source of truth for how each `DataGridDataType` renders on screen
 * and how it's formatted in Excel export. A column with `dataType: 'currency'`
 * inherits right-alignment, a French-locale money renderer AND an xlsx
 * `#,##0.00 "EUR"` format — all at once.
 *
 * Columns can still override individual aspects (`align`, `cell`, …) but
 * when they don't, this is where the defaults come from.
 */

import type { ReactNode } from 'react';
import type {
  ColumnAlign,
  DataGridColumn,
  DataGridDataType,
  DataGridSummaryItem,
} from './types';
import { computeSummary } from './grouping';

/** Default precision per data type. */
const DEFAULT_PRECISION: Record<DataGridDataType, number> = {
  text: 0,
  integer: 0,
  number: 2,
  currency: 2,
  percent: 2,
  date: 0,
  datetime: 0,
  duration: 0,
  boolean: 0,
  slot: 0,
};

const DEFAULT_ALIGN: Record<DataGridDataType, ColumnAlign> = {
  text: 'left',
  integer: 'right',
  number: 'right',
  currency: 'right',
  percent: 'right',
  date: 'left',
  datetime: 'left',
  duration: 'right',
  boolean: 'center',
  slot: 'center',
};

/**
 * Resolve a column's effective alignment: explicit `align` wins, otherwise
 * the type default, otherwise `left`.
 */
export function resolveAlign<T>(col: DataGridColumn<T>): ColumnAlign {
  if (col.align) return col.align;
  if (col.dataType) return DEFAULT_ALIGN[col.dataType];
  return 'left';
}

/**
 * Effective precision for a column: column-level override > type default.
 */
export function resolvePrecision<T>(col: DataGridColumn<T>): number {
  if (col.precision !== undefined) return col.precision;
  if (col.dataType) return DEFAULT_PRECISION[col.dataType];
  return 0;
}

// ----------------------------------------------------------------------------
// Display formatters — value → string | ReactNode
// ----------------------------------------------------------------------------

/** French locale number formatter with a configurable decimal count. */
function formatNumber(value: number, decimals: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

function formatCurrency(value: number, currency: string, decimals: number): string {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch {
    // Fallback for non-ISO currency codes — show plain number with symbol suffix
    return `${formatNumber(value, decimals)} ${currency}`;
  }
}

function formatPercent(value: number, decimals: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

function formatDate(value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR');
}

function formatDateTime(value: Date | string | number): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Minutes → "Xh YYm" (or "YYm" for short values). Matches BStaff convention. */
function formatDurationMinutes(minutes: number): string {
  const rounded = Math.round(minutes);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m}m`;
  return `${sign}${h}h${m.toString().padStart(2, '0')}`;
}

function formatBoolean(value: unknown): string {
  return value ? 'Oui' : 'Non';
}

/**
 * Render the cell content for a typed column. Returns `'—'` when the value
 * is null/undefined (except for `boolean` which always renders `'Non'`).
 *
 * For `currency` columns the effective currency is resolved in this order :
 *   1. `col.currency` (per-column override)
 *   2. `defaultCurrency` (grid-level default, typically from `useCurrency()`)
 *   3. `'EUR'` hard fallback
 *
 * so callers can set the grid currency once at the `<DataGrid currency=…/>`
 * level and every money column picks it up automatically — matching the
 * way `exportable.currency` flows for the Excel side.
 */
export function renderTypedCell<T>(
  col: DataGridColumn<T>,
  value: string | number | Date | null | undefined,
  defaultCurrency: string = 'EUR'
): ReactNode {
  if (!col.dataType) return value === null || value === undefined ? '—' : String(value);

  if (col.dataType === 'boolean') return formatBoolean(value);

  if (value === null || value === undefined) return '—';

  const precision = resolvePrecision(col);
  switch (col.dataType) {
    case 'text':
      return String(value);
    case 'integer':
      return formatNumber(Number(value), 0);
    case 'number':
      return formatNumber(Number(value), precision);
    case 'currency':
      return formatCurrency(
        Number(value),
        col.currency ?? defaultCurrency,
        precision
      );
    case 'percent':
      return formatPercent(Number(value), precision);
    case 'date':
      return formatDate(value as Date | string | number);
    case 'datetime':
      return formatDateTime(value as Date | string | number);
    case 'duration':
      return formatDurationMinutes(Number(value));
    default:
      return String(value);
  }
}

// ----------------------------------------------------------------------------
// Excel numFmt — column type → Excel format string
// ----------------------------------------------------------------------------

/**
 * Resolve an Excel `numFmt` string for a typed column, or `undefined` for
 * text-like types where no format is needed. Called once per column when
 * building the workbook, and applied on the column's `style.numFmt`.
 */
export function resolveExcelNumFmt<T>(
  col: DataGridColumn<T>,
  defaultCurrency: string
): string | undefined {
  if (!col.dataType) return undefined;
  const precision = resolvePrecision(col);
  const decimals = (n: number) => (n === 0 ? '' : `.${'0'.repeat(n)}`);

  switch (col.dataType) {
    case 'text':
    case 'boolean':
    case 'slot':
      return undefined;
    case 'integer':
      return '#,##0';
    case 'number':
      return `#,##0${decimals(precision)}`;
    case 'currency': {
      const cur = col.currency ?? defaultCurrency;
      return `#,##0${decimals(precision)} "${cur}"`;
    }
    case 'percent':
      return `0${decimals(precision)}%`;
    case 'date':
      return 'dd/mm/yyyy';
    case 'datetime':
      return 'dd/mm/yyyy hh:mm';
    case 'duration':
      // Excel stores durations as day fractions; we divide minutes by 1440
      // at export time. `[h]:mm` gère les valeurs ≥ 24h mais PAS les valeurs
      // négatives (Excel affiche `####`) — d'où le format 3-part
      // `positive;negative;zero` qui autorise le signe (ex "Reste à effectuer"
      // = base contrat − heures faites, négatif en cas d'heures sup).
      return '[h]:mm;-[h]:mm;0';
    default:
      return undefined;
  }
}

/**
 * Render the inner content of a summary cell (subtotal or grand-total)
 * for a column. Priority :
 *   1. `item.render(value, rows)` if the caller provided a custom renderer
 *   2. Otherwise run the COLUMN's typed renderer so money subtotals keep
 *      their currency suffix, percents stay `0.00%`, durations `hh:mm`, etc.
 *   3. `null` when no summary item targets this column — the caller then
 *      decides what to show (typically a "Total" label on the first column).
 *
 * Lives here so data rows and summary rows share the exact same
 * formatting rules — zero chance of divergence as new `dataType`s
 * are added.
 */
export function renderSummaryCellContent<T>(
  column: DataGridColumn<T>,
  summary: DataGridSummaryItem<T>[],
  rows: T[],
  currency: string
): ReactNode {
  const item = summary.find((s) => s.columnId === column.id);
  if (!item) return null;
  const value = computeSummary(item, column, rows);
  if (item.render) return item.render(value, rows);
  if (value === null) return '—';
  return renderTypedCell(column, value, currency);
}

/**
 * Transform a raw value into its Excel-native representation for a given
 * type. Most types pass through, but `duration` must be converted from
 * minutes to day fractions to match the `[h]:mm` format.
 */
export function toExcelValue<T>(
  col: DataGridColumn<T>,
  value: string | number | Date | null | undefined
): string | number | Date | null {
  if (value === null || value === undefined) return null;
  if (!col.dataType) return value instanceof Date ? value : (value as string | number);

  switch (col.dataType) {
    case 'slot':
      // Slot cells carry no text in Excel — the blue fill (applied by the
      // writer from `col.slotValue`) is the only signal. `exportValue`
      // still returns '●' / '' for CSV / sort, but that text is
      // deliberately dropped here.
      return null;
    case 'duration':
      // Arrondir à la minute AVANT la conversion pour éviter que la précision
      // float produise `-0,291666666666667` dans la barre de formule Excel
      // (visuellement ok grâce au format, mais peut désarçonner l'utilisateur
      // qui clique sur la cellule).
      return Math.round(Number(value)) / 1440;
    case 'date':
    case 'datetime': {
      if (value instanceof Date) return value;
      const d = new Date(value as string | number);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    case 'boolean':
      return value ? 'Oui' : 'Non';
    default:
      return value instanceof Date ? value : (value as string | number);
  }
}
